import "./results/styles.css";
import type {
  AssessCandidateInput,
  AssessCandidateResponse,
  CandidateAssessment,
} from "@shared/labs/candidate-assessment";
import { DEMO_CANDIDATE_ASSESSMENT } from "@shared/labs/candidate-assessment";
import { getDiscipline } from "@shared/labs/disciplines/registry";
import type { FrameworkInfo } from "@shared/labs/evaluation-settings-schemas";
import type { CorrectionCallback, ThreeAxisRecommendation } from "@shared/labs/three-axis";
import {
  DEMO_BORDERLINE,
  DEMO_INTERVIEW,
  DEMO_INTERVIEW_EXCEPTION,
  DEMO_NO_INTERVIEW,
} from "@shared/labs/three-axis-demo";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { ProfileHistoryDrawer } from "../evaluation-settings/profile-history-drawer";
import { FormView } from "./form-view";
import { type PreviewIdentity, PreviewView } from "./preview-view";
import { ProcessingView, type StreamProgress } from "./processing-view";
import { ThreeAxisReport } from "./results";
import {
  clearAssessment,
  clearIdFromUrl,
  loadAssessment,
  newAssessmentId,
  readIdFromUrl,
  saveAssessment,
  writeIdToUrl,
} from "./state-persistence";
import { useCorrectionHandler } from "./use-correction-handler";
import { isValidRegistrySlug, useDiscipline } from "./use-discipline";

type View = "form" | "preview" | "processing" | "results";

const INITIAL_PROGRESS: StreamProgress = {
  pdfExtracted: false,
  classificationReady: false,
  profileReady: false,
  scoringReady: false,
  positionFitReady: false,
  marketLevelReady: false,
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Parse SSE text chunk into individual events */
function parseSSEChunk(text: string): { event: string; data: any }[] {
  const events: { event: string; data: any }[] = [];
  let currentEvent = "";
  let currentData = "";

  for (const line of text.split("\n")) {
    if (line.startsWith("event: ")) {
      currentEvent = line.slice(7).trim();
    } else if (line.startsWith("data: ")) {
      currentData = line.slice(6);
    } else if (line === "" && currentEvent && currentData) {
      try {
        events.push({ event: currentEvent, data: JSON.parse(currentData) });
      } catch {
        // ignore malformed JSON
      }
      currentEvent = "";
      currentData = "";
    }
  }
  return events;
}

/**
 * Read-only demo shortcut: `?demo=interview`, `?demo=no-interview`,
 * `?demo=exception` (hard exception-lane: high GCA carries despite mediocre
 * Comp Way), `?demo=borderline` (soft-cutoff lane: GCA in [0.80, 0.85)
 * surfaces as interview but flagged for human triage), `?demo=processing`
 * or `?demo=preview` renders a preset view directly, bypassing the form +
 * LLM pipeline. Used by Playwright visual-regression baselines and for
 * quick design review.
 */
type DemoMode =
  | "interview"
  | "no-interview"
  | "exception"
  | "borderline"
  | "processing"
  | "preview"
  | "confirm"
  | "prior";

function readDemoParam(): DemoMode | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("demo");
  if (
    v === "interview" ||
    v === "no-interview" ||
    v === "exception" ||
    v === "borderline" ||
    v === "processing" ||
    v === "preview" ||
    v === "confirm" ||
    v === "prior"
  ) {
    return v;
  }
  return null;
}

/** Resolve the display name of the discipline a recommendation is for.
 *  Falls back to "Forward Deployed Engineer" for legacy snapshots without
 *  disciplineSlug — the only discipline that existed pre-2026-05-12. */
function disciplineNameFor(rec: { disciplineSlug?: string }): string {
  const slug = rec.disciplineSlug ?? "forward-deployed";
  return getDiscipline(slug)?.name ?? "Forward Deployed Engineer";
}

function ProcessingDemo() {
  const [progress, setProgress] = useState<StreamProgress>(INITIAL_PROGRESS);
  const [resultReady, setResultReady] = useState(false);
  const [startKey, setStartKey] = useState(0);

  // Walk through the SSE phases at ~1.4s cadence so the arc and sonar rings
  // have time to read on a design review. startKey bumps to restart the demo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: startKey is a restart trigger
  useEffect(() => {
    setProgress(INITIAL_PROGRESS);
    setResultReady(false);
    const timers: number[] = [];
    const schedule = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };
    schedule(1200, () => setProgress((p) => ({ ...p, pdfExtracted: true })));
    schedule(2600, () => setProgress((p) => ({ ...p, classificationReady: true })));
    schedule(4000, () => setProgress((p) => ({ ...p, profileReady: true })));
    schedule(5400, () => setProgress((p) => ({ ...p, scoringReady: true })));
    schedule(6800, () => setProgress((p) => ({ ...p, positionFitReady: true })));
    schedule(7600, () => setProgress((p) => ({ ...p, marketLevelReady: true })));
    schedule(8200, () => setResultReady(true));
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [startKey]);

  return (
    <div>
      <ProcessingView
        assessment={DEMO_CANDIDATE_ASSESSMENT}
        resultReady={resultReady}
        streamProgress={progress}
        onShowResults={() => setStartKey((k) => k + 1)}
        onCancel={() => setStartKey((k) => k + 1)}
      />
    </div>
  );
}

export default function LabsCandidateIntelligence() {
  const demoMode = readDemoParam();
  if (demoMode === "processing") {
    return <ProcessingDemo />;
  }
  if (demoMode === "preview") {
    return <PreviewFlowDemo />;
  }
  if (demoMode === "confirm") {
    return <ConfirmDemo />;
  }
  if (demoMode === "prior") {
    return <PriorDialogDemo />;
  }
  if (
    demoMode === "interview" ||
    demoMode === "no-interview" ||
    demoMode === "exception" ||
    demoMode === "borderline"
  ) {
    return <DemoReport mode={demoMode} />;
  }
  return <LabsCandidateIntelligenceInner />;
}

/** Demo route that wires the real `useCorrectionHandler` so Playwright can
 *  drive the preview-then-commit flow end-to-end. The assessmentId is a
 *  recognizable nil UUID so both mocked specs and real-backend specs can drive
 *  this route — the backend accepts any UUID shape for `assessment_id` since it
 *  isn't yet an FK. */
const DEMO_PREVIEW_ASSESSMENT_ID = "00000000-0000-0000-0000-000000000d3e";
function PreviewFlowDemo() {
  const [rec, setRec] = useState<ThreeAxisRecommendation>(DEMO_NO_INTERVIEW);
  const handleCorrection = useCorrectionHandler({
    assessmentId: DEMO_PREVIEW_ASSESSMENT_ID,
    threeAxis: rec,
    setThreeAxis: setRec,
  });
  return (
    <ThreeAxisReport
      recommendation={rec}
      candidateName="Candidato Demo · Preview Flow"
      discipline={disciplineNameFor(rec)}
      onCorrection={handleCorrection}
    />
  );
}

/** Demo route for the candidate-identity confirmation screen (the
 *  "É esse o candidato?" card shown after URL lookup, before the pipeline
 *  runs). Static identity so the layout can be reviewed in isolation. */
function ConfirmDemo() {
  const [confirming, setConfirming] = useState(false);
  return (
    <PreviewView
      identity={{
        username: "lucascavalcanti",
        fullName: "Lucas Cavalcanti",
        headline: "Product Operations Senior Analyst · 99",
        location: "São Paulo, Brazil",
        email: "",
        followerCount: 1300,
        connectionsCount: 1300,
        currentRole: "Product Operations Senior Analyst",
        currentCompany: "99",
        positionsCount: 4,
        educationsCount: 1,
      }}
      pdfFile={null}
      confirming={confirming}
      onConfirm={() => {
        setConfirming(true);
        window.setTimeout(() => setConfirming(false), 1200);
      }}
      onBack={() => undefined}
    />
  );
}

/** Demo route for the "avaliação anterior encontrada" modal. Renders the
 *  dialog over the form view so the scrim + card placement can be reviewed
 *  without going through the full pipeline. */
function PriorDialogDemo() {
  const [open, setOpen] = useState(true);
  return (
    <>
      <LabsCandidateIntelligenceInner />
      <PriorEvaluationDialog
        open={open}
        prior={{
          assessmentId: "00000000-0000-0000-0000-000000000abc",
          candidateName: "Lucas Cavalcanti",
          disciplineSlug: "forward-deployed",
          createdBy: null,
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 60).toISOString(),
          summary: { recommendedLevel: "L3" },
        }}
        onOpenPrior={() => setOpen(false)}
        onRegenerate={() => setOpen(false)}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}

/** Demo path: holds the recommendation in local state so the "ajustar" UI can
 *  reflect overrides for evidence + axis + skill corrections without reaching
 *  the backend. Mirrors the client-side branches of `useCorrectionHandler`. */
function DemoReport({ mode }: { mode: "interview" | "no-interview" | "exception" | "borderline" }) {
  const initialRec =
    mode === "interview"
      ? DEMO_INTERVIEW
      : mode === "exception"
        ? DEMO_INTERVIEW_EXCEPTION
        : mode === "borderline"
          ? DEMO_BORDERLINE
          : DEMO_NO_INTERVIEW;
  const [rec, setRec] = useState<ThreeAxisRecommendation>(initialRec);
  const handleCorrection: CorrectionCallback = async (input) => {
    const kind =
      input.kind ??
      (rec.evidences.some((e) => e.id === input.evidenceId)
        ? "evidence"
        : rec.axes.some((a) => a.id === input.evidenceId)
          ? "axis"
          : "skill");

    if (kind === "skill") {
      const lvl = Math.max(1, Math.min(5, Math.round(input.newScore))) as 1 | 2 | 3 | 4 | 5;
      const assignedKey = `L${lvl}` as const;
      setRec({
        ...rec,
        frameworkSkills: rec.frameworkSkills.map((s) =>
          s.id === input.evidenceId ? { ...s, assigned: assignedKey } : s,
        ),
      });
      return undefined;
    }

    if (kind === "axis") {
      const clamped = Math.max(0, Math.min(1, input.newScore));
      setRec({
        ...rec,
        axes: rec.axes.map((a) => {
          if (a.id !== input.evidenceId) return a;
          const originalScore = a.chroCorrection?.originalScore ?? a.value;
          return {
            ...a,
            value: clamped,
            pass: clamped >= a.threshold,
            margin: clamped - a.threshold,
            closeCall: Math.abs(clamped - a.threshold) <= 0.05,
            chroCorrection: {
              originalScore,
              correctedScore: clamped,
              rationale: input.rationale,
              correctedAt: new Date().toISOString(),
              correctedBy: "chro",
            },
          };
        }),
      });
      return undefined;
    }

    // evidence — 1-5 star scale (1 = no signal, 5 = exceptional)
    const clamped = Math.max(1, Math.min(5, input.newScore));
    setRec({
      ...rec,
      evidences: rec.evidences.map((e) => {
        if (e.id !== input.evidenceId) return e;
        const originalScore = e.chroCorrection?.originalScore ?? e.score ?? 0;
        return {
          ...e,
          score: clamped,
          chroCorrection: {
            originalScore,
            correctedScore: clamped,
            rationale: input.rationale,
            correctedAt: new Date().toISOString(),
            correctedBy: "chro",
          },
        };
      }),
    });
    return undefined;
  };
  return (
    <ThreeAxisReport
      recommendation={rec}
      candidateName={
        mode === "interview"
          ? "Candidato Demo · Entrevistar"
          : mode === "exception"
            ? "Candidato Demo · Excepcionalidade"
            : mode === "borderline"
              ? "Candidato Demo · Borderline"
              : "Candidato Demo · Não entrevistar"
      }
      discipline={disciplineNameFor(rec)}
      onCorrection={handleCorrection}
    />
  );
}

function LabsCandidateIntelligenceInner() {
  const { toast } = useToast();
  const { isSuperAdmin, isManager, hasLabsAccess } = useAuth();
  // Same gate the evaluation-settings page enforces — mirrors the server's
  // requireLabsAccess so the in-app "Configurações" entry point doesn't get
  // hidden from labs-role users who can actually use it.
  const canOpenSettings = isSuperAdmin || isManager || hasLabsAccess;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [view, setView] = useState<View>("form");
  const [inputMode, setInputMode] = useState<"pdf" | "url">("url");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [hasReferral, setHasReferral] = useState(false);
  const [refFrom, setRefFrom] = useState("");
  const [refRole, setRefRole] = useState("");
  const [refNote, setRefNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Discipline state is managed by `useDiscipline`. The hook is permissive
  // by design — the form picker is driven by the live `frameworks` list
  // (DB-backed scoring_configs), which may include slugs not yet registered
  // as a runtime DisciplineSpec. URL hydration validates via
  // `isValidRegistrySlug` before applying so a stale `?discipline=` can't
  // poison state.
  const urlInitial = (() => {
    if (typeof window === "undefined") return undefined;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("discipline");
    return fromUrl && isValidRegistrySlug(fromUrl) ? fromUrl : undefined;
  })();
  const { slug: discipline, setSlug: setDiscipline } = useDiscipline(urlInitial);
  const [positionContext, setPositionContext] = useState("");
  const [previewIdentity, setPreviewIdentity] = useState<PreviewIdentity | null>(null);
  /** When a candidate already has an archive row, we pause the "confirm"
   *  action and let the CHRO pick: open the stored evaluation, or kick off a
   *  new one. null = no prompt. */
  const [priorEvaluation, setPriorEvaluation] = useState<PriorEvaluationRow | null>(null);

  const { data: frameworks } = useQuery<FrameworkInfo[]>({
    // biome-ignore lint/security/noSecrets: query key
    queryKey: ["labs", "evaluationSettingsListFrameworks"],
    queryFn: () => rpcClient.labs.evaluationSettingsListFrameworks(),
  });

  const [result, setResult] = useState<AssessCandidateResponse | null>(null);
  const [previewAssessment, setPreviewAssessment] =
    useState<CandidateAssessment>(DEMO_CANDIDATE_ASSESSMENT);
  const [streamProgress, setStreamProgress] = useState<StreamProgress>(INITIAL_PROGRESS);
  const assessmentIdRef = useRef<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Restore a prior completed evaluation from localStorage when the URL
  // carries an id. Mid-stream reloads intentionally fall back to the form —
  // we don't try to reattach to an abandoned SSE stream.
  //
  // Note: `?id=` is overloaded. It can be either:
  //   (a) a local-state cache key written by this page itself (the common
  //       case — survives reload), or
  //   (b) a `labs_evaluations.id` someone hand-typed to share a profile.
  //
  // This effect handles (a). The deep-link effect below handles (b) when
  // (a) misses. We deliberately do NOT clear the URL on a localStorage miss
  // anymore — the deep-link effect needs to see the param to attempt (b).
  useEffect(() => {
    const id = readIdFromUrl();
    if (!id) return;
    const cached = loadAssessment(id);
    if (!cached) {
      // No local cache. Don't clear the URL — let the deep-link effect
      // try fetching ?id= as a labs_evaluations.id. If that also fails,
      // openPriorEvaluation surfaces a toast and the URL gets rewritten
      // by writeIdToUrl on the next successful operation.
      return;
    }
    assessmentIdRef.current = id;
    setResult(cached.result);
    setPreviewAssessment(cached.result.assessment);
    setLinkedinUrl(cached.linkedinUrl);
    setDiscipline(cached.discipline);
    setPositionContext(cached.positionContext);
    setView("results");

    if (!cached.result.archivedEvaluationId) {
      const url = cached.result.assessment.linkedinUrl;
      const name = cached.result.assessment.candidateName;
      if (url || name) {
        const params = new URLSearchParams();
        if (url) params.set("linkedinUrl", url);
        if (name) params.set("candidateName", name);
        rpcClient.labs
          .evaluationsFindExisting({
            linkedinUrl: url || undefined,
            candidateName: name || undefined,
          })
          .then((rows: { id: string }[]) => {
            const hit = rows[0];
            if (!hit) return;
            setResult((prev) => {
              if (!prev) return prev;
              const next: AssessCandidateResponse = { ...prev, archivedEvaluationId: hit.id };
              saveAssessment(id, {
                result: next,
                linkedinUrl: cached.linkedinUrl,
                discipline: cached.discipline,
                positionContext: cached.positionContext,
              });
              return next;
            });
          })
          .catch(() => {});
      }
    }
    // setDiscipline comes from useDiscipline — wrapped in useCallback with no
    // deps, so the reference is stable across renders. useState setters that
    // also appear in the effect (setResult, setLinkedinUrl, etc.) are
    // recognized as stable by biome's exhaustive-deps heuristic; setDiscipline
    // needs to be listed explicitly to satisfy the same check.
  }, [setDiscipline]);

  // Deep-link entry point: when the page is opened with
  // ?evaluationId=<labs_evaluations.id> (or `?id=` when the value is NOT
  // a known localStorage cache key), hydrate that archived eval into the
  // report view. Drives the "Abrir relatório completo" jumps from the
  // archive list — reviewers click a row over there and land here with
  // the full assessment already loaded, no LinkedIn re-fetch.
  //
  // The `?id=` alias makes hand-crafted share URLs work: a reviewer
  // typing `?id=<uuid>` to share a profile gets the same behavior as
  // `?evaluationId=<uuid>`. The check against loadAssessment short-
  // circuits when ?id= was the local-state mechanism (the previous
  // effect already handled it).
  // biome-ignore lint/correctness/useExhaustiveDependencies: openPriorEvaluation is stable
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const explicitEvalId = params.get("evaluationId");
    const idAlias = params.get("id");
    // ?id= is the deep-link target only when it doesn't match a
    // localStorage cache key (i.e. the previous effect treated it as a
    // local id and bailed because there was no cached state).
    const aliasIsDeepLink = idAlias && !loadAssessment(idAlias);
    const evalId = explicitEvalId ?? (aliasIsDeepLink ? idAlias : null);
    if (!evalId) return;
    void openPriorEvaluation(evalId);
    // Strip the deep-link params so a refresh doesn't re-trigger this
    // load on top of the localStorage-restore the other useEffect runs.
    const next = new URL(window.location.href);
    next.searchParams.delete("evaluationId");
    next.searchParams.delete("id");
    window.history.replaceState({}, "", next.toString());
  }, []);

  const startStreaming = useCallback(async () => {
    const payload: AssessCandidateInput = {
      linkedinUrl: inputMode === "url" ? linkedinUrl || undefined : undefined,
      referral: hasReferral ? { from: refFrom, role: refRole, note: refNote } : null,
      position: {
        discipline,
        additionalContext: positionContext || undefined,
      },
    };
    if (pdfFile) {
      payload.profilePdf = {
        fileName: pdfFile.name,
        fileContent: await fileToBase64(pdfFile),
        fileType: pdfFile.type,
        fileSize: pdfFile.size,
      };
    }

    const abort = new AbortController();
    abortRef.current = abort;

    try {
      const response = await fetch("/api/labs/candidate-intelligence/assess/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: abort.signal,
        credentials: "include",
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // Collect all events from a chunk, then apply them with staggered delays
      // so React processes each state update in a separate render cycle.
      // This is necessary because SSE events may arrive as a single chunk
      // (server-side buffering), causing React to batch all updates into one render.
      const applyEvent = (event: string, data: any) => {
        switch (event) {
          case "pdf_extracted":
            setStreamProgress((prev) => ({ ...prev, pdfExtracted: true }));
            break;
          case "classification_ready":
            setStreamProgress((prev) => ({
              ...prev,
              classificationReady: true,
              classificationData: data,
            }));
            break;
          case "profile_ready":
            setStreamProgress((prev) => ({
              ...prev,
              profileReady: true,
              profileData: data,
            }));
            break;
          case "scoring_ready":
            setStreamProgress((prev) => ({
              ...prev,
              scoringReady: true,
              scoringData: data,
            }));
            break;
          case "position_fit_ready":
            setStreamProgress((prev) => ({
              ...prev,
              positionFitReady: true,
              positionFitData: data,
            }));
            break;
          case "market_level_ready":
            setStreamProgress((prev) => ({
              ...prev,
              marketLevelReady: true,
              marketLevelData: data,
            }));
            break;
          case "done": {
            // The server archives synchronously before sending "done", so
            // `responseData.archivedEvaluationId` is already populated when
            // archiving succeeded — the client no longer needs a separate
            // "archived" event or a post-reload /existing recovery.
            const responseData = data as AssessCandidateResponse;
            setResult(responseData);
            setPreviewAssessment(responseData.assessment);
            // Prefer the server-archived `labs_evaluations.id` as both the
            // localStorage cache key AND the URL `?id=` param. That makes
            // the URL shareable: a recipient who hits the link with no
            // localStorage entry falls through to the deep-link effect,
            // which calls `evaluationsGet` against the same id and renders
            // the archived row. Pre-patch, `?id=` was a random local UUID
            // that only existed on the originator's browser, so every
            // share returned 404 + "Não foi possível abrir a avaliação
            // anterior". Fallback chain matters: keep the prior local id
            // when the archive failed (responseData.archivedEvaluationId
            // is undefined) so the operator's reload still recovers.
            const prior = assessmentIdRef.current;
            const id =
              responseData.archivedEvaluationId ?? assessmentIdRef.current ?? newAssessmentId();
            if (prior && prior !== id) clearAssessment(prior);
            assessmentIdRef.current = id;
            saveAssessment(id, {
              result: responseData,
              linkedinUrl,
              discipline,
              positionContext,
            });
            writeIdToUrl(id);
            break;
          }
          case "error":
            throw new Error(data.message || "Erro na avaliação");
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Extract complete SSE events from buffer
        const events = parseSSEChunk(buffer);
        // Keep only the unparsed tail
        const lastDoubleNewline = buffer.lastIndexOf("\n\n");
        buffer = lastDoubleNewline >= 0 ? buffer.slice(lastDoubleNewline + 2) : buffer;

        if (events.length === 0) continue;

        if (events.length === 1) {
          // Single event — apply immediately
          applyEvent(events[0].event, events[0].data);
        } else {
          // Multiple events arrived in one chunk — stagger them
          // so React gets separate render cycles for each
          for (let i = 0; i < events.length; i++) {
            const { event, data } = events[i];
            if (i === 0) {
              applyEvent(event, data);
            } else {
              await new Promise<void>((resolve) => {
                setTimeout(() => {
                  applyEvent(event, data);
                  resolve();
                }, i * 500); // 500ms gap between staggered events
              });
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return; // user cancelled
      toast({
        title: "Não foi possível gerar avaliação",
        description: (err as Error).message,
        variant: "destructive",
      });
      setView("form");
    } finally {
      setSubmitting(false);
      abortRef.current = null;
    }
  }, [
    inputMode,
    linkedinUrl,
    pdfFile,
    hasReferral,
    refFrom,
    refRole,
    refNote,
    discipline,
    positionContext,
    toast,
  ]);

  async function handleSubmit() {
    if (!discipline) {
      toast({
        title: "Disciplina obrigatória",
        description: "Selecione a disciplina para avaliar.",
        variant: "destructive",
      });
      return;
    }
    if (inputMode === "pdf" && !pdfFile) {
      toast({
        title: "PDF do LinkedIn obrigatório",
        description: "Nesta versão alpha, a avaliação precisa do export em PDF do perfil LinkedIn.",
        variant: "destructive",
      });
      return;
    }
    if (inputMode === "url" && !linkedinUrl.trim()) {
      toast({
        title: "URL do LinkedIn obrigatória",
        description: "Informe a URL ou username do perfil LinkedIn para avaliar.",
        variant: "destructive",
      });
      return;
    }

    // Both URL and PDF modes → show identity preview first so the user can
    // confirm they're running the full LLM pipeline against the right profile.
    setSubmitting(true);
    try {
      let identity: PreviewIdentity;
      if (inputMode === "url") {
        identity = (await rpcClient.labs.previewIdentity({ linkedinUrl })) as PreviewIdentity;
      } else {
        if (!pdfFile) throw new Error("PDF ausente");
        const fileContent = await fileToBase64(pdfFile);
        const res = await fetch("/api/labs/candidate-intelligence/preview-identity-pdf", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            profilePdf: { fileName: pdfFile.name, fileContent },
          }),
          credentials: "include",
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(err.error || "Falha ao ler PDF");
        }
        identity = (await res.json()) as PreviewIdentity;
      }
      setPreviewIdentity(identity);
      setView("preview");
    } catch (err) {
      toast({
        title: "Não foi possível preparar a avaliação",
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  function startFullEvaluation() {
    assessmentIdRef.current = null;
    clearIdFromUrl();
    setPreviewAssessment(DEMO_CANDIDATE_ASSESSMENT);
    setResult(null);
    setStreamProgress(INITIAL_PROGRESS);
    setSubmitting(true);
    setView("processing");
    startStreaming();
  }

  /**
   * Front-door for the preview confirm button. Checks the evaluation
   * archive for this candidate; if anything matches we open the dialog and
   * let the CHRO decide, otherwise we go straight to the streaming run.
   */
  async function startEvaluationMaybeWithPrompt() {
    const url = linkedinUrl.trim();
    const nameFromPreview = previewIdentity?.fullName?.trim();
    const params = new URLSearchParams();
    if (url) params.set("linkedinUrl", url);
    if (nameFromPreview) params.set("candidateName", nameFromPreview);
    if (!url && !nameFromPreview) {
      startFullEvaluation();
      return;
    }
    try {
      const rows = (await rpcClient.labs.evaluationsFindExisting({
        linkedinUrl: url || undefined,
        candidateName: nameFromPreview || undefined,
      })) as PriorEvaluationRow[];
      if (rows.length === 0) {
        startFullEvaluation();
        return;
      }
      setPriorEvaluation(rows[0]);
    } catch {
      // Network hiccup — don't block the evaluation, just proceed.
      startFullEvaluation();
    }
  }

  /**
   * Opens a prior archived evaluation without rerunning the pipeline.
   * Hydrates the results view with the stored assessment + archive id so
   * the "Usar como referência" bar still has a valid target.
   */
  async function openPriorEvaluation(id: string) {
    try {
      const body = (await rpcClient.labs.evaluationsGet({ id })) as {
        id: string;
        assessment: CandidateAssessment;
      };
      const restored: AssessCandidateResponse = {
        assessment: body.assessment,
        mocked: false,
        archivedEvaluationId: body.id,
      };
      // Use the archived `labs_evaluations.id` as the canonical URL +
      // localStorage cache key so a re-share of THIS page produces a
      // working URL (the original symptom was that openPriorEvaluation
      // minted a fresh random id, breaking the share-link round-trip).
      // Drop any prior local-only key so the orphaned localStorage
      // entry doesn't accrete.
      const prior = assessmentIdRef.current;
      if (prior && prior !== body.id) clearAssessment(prior);
      assessmentIdRef.current = body.id;
      saveAssessment(body.id, {
        result: restored,
        linkedinUrl,
        discipline,
        positionContext,
      });
      writeIdToUrl(body.id);
      setResult(restored);
      setPreviewAssessment(body.assessment);
      setView("results");
    } catch (err) {
      toast({
        title: "Não foi possível abrir a avaliação anterior",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  }

  function reset() {
    abortRef.current?.abort();
    const prior = assessmentIdRef.current;
    if (prior) clearAssessment(prior);
    assessmentIdRef.current = null;
    clearIdFromUrl();
    setView("form");
    setResult(null);
    setStreamProgress(INITIAL_PROGRESS);
    setSubmitting(false);
    setPreviewIdentity(null);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast({
        title: "Arquivo inválido",
        description: "Envie o export do LinkedIn em formato PDF.",
        variant: "destructive",
      });
      return;
    }
    setPdfFile(file);
  }

  // Lifted up from `ResultsView` so the trigger can sit next to the
  // Settings gear at the top of the page (where reviewers expect
  // admin/meta actions to live), while keeping the drawer + state owned
  // at the page level — there's only ever one drawer in flight per page.
  const [historyOpen, setHistoryOpen] = useState(false);
  const archivedEvaluationId = view === "results" ? (result?.archivedEvaluationId ?? null) : null;
  const candidateNameForHistory = result?.assessment.candidateName || "Candidato";

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p
            className="mb-1 font-semibold uppercase"
            style={{
              fontSize: "11px",
              letterSpacing: 0,
              lineHeight: 1.4,
              color: "var(--ink-muted)",
            }}
          >
            Alpha · Labs
          </p>
          <PageHeader
            title="Inteligência de Candidatos"
            description="Avaliação automatizada de candidatos via perfil LinkedIn contra frameworks de skills."
          />
        </div>
        <div className="flex items-center gap-2">
          {/* "Histórico" opens the corrections audit — a build/DS affordance.
              Hidden from regular platform users on the open "use" surface, same
              rule as the Settings gear next to it. */}
          {archivedEvaluationId && canOpenSettings && (
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-3 text-sm font-semibold"
              onClick={() => setHistoryOpen(true)}
              data-testid="open-history-drawer"
              title="Ver todas as correções e mudanças nesta avaliação"
            >
              Histórico
            </Button>
          )}
          {canOpenSettings && (
            <Link href="/labs/evaluation-settings">
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-3 text-sm font-semibold"
                title="Configurações de avaliação"
              >
                Configurações
              </Button>
            </Link>
          )}
        </div>
      </div>
      <ProfileHistoryDrawer
        open={historyOpen}
        evaluationId={archivedEvaluationId}
        candidateName={candidateNameForHistory}
        onClose={() => setHistoryOpen(false)}
      />

      {view === "form" && (
        <FormView
          inputMode={inputMode}
          setInputMode={setInputMode}
          linkedinUrl={linkedinUrl}
          setLinkedinUrl={setLinkedinUrl}
          pdfFile={pdfFile}
          setPdfFile={setPdfFile}
          hasReferral={hasReferral}
          setHasReferral={setHasReferral}
          refFrom={refFrom}
          setRefFrom={setRefFrom}
          refRole={refRole}
          setRefRole={setRefRole}
          refNote={refNote}
          setRefNote={setRefNote}
          fileInputRef={fileInputRef}
          onFileChange={handleFileChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          discipline={discipline}
          setDiscipline={setDiscipline}
          positionContext={positionContext}
          setPositionContext={setPositionContext}
          frameworks={frameworks ?? []}
        />
      )}

      {view === "preview" && previewIdentity && (
        <PreviewView
          identity={previewIdentity}
          pdfFile={inputMode === "pdf" ? pdfFile : null}
          confirming={submitting}
          onBack={() => {
            setPreviewIdentity(null);
            setView("form");
          }}
          onConfirm={() => {
            // Before burning a full pipeline run, check if this candidate
            // already has an archived evaluation. If so, the user picks
            // between opening the prior result or generating a fresh one.
            startEvaluationMaybeWithPrompt();
          }}
        />
      )}
      {priorEvaluation && (
        <PriorEvaluationDialog
          open={priorEvaluation !== null}
          prior={priorEvaluation}
          onOpenPrior={async () => {
            const id = priorEvaluation.id;
            setPriorEvaluation(null);
            await openPriorEvaluation(id);
          }}
          onRegenerate={() => {
            setPriorEvaluation(null);
            startFullEvaluation();
          }}
          onCancel={() => setPriorEvaluation(null)}
        />
      )}

      {view === "processing" && (
        <ProcessingView
          assessment={previewAssessment}
          resultReady={result !== null}
          extractedTextPreview={result?.extractedTextPreview}
          extractedCharCount={result?.extractedCharCount}
          llmEvaluation={result?.llmEvaluation}
          streamProgress={streamProgress}
          onShowResults={() => setView("results")}
          onCancel={reset}
        />
      )}

      {view === "results" && result?.assessment.threeAxis && (
        <ResultsView
          result={result}
          onThreeAxisChange={(next) =>
            setResult((prev) => {
              if (!prev) return prev;
              const updated = { ...prev, assessment: { ...prev.assessment, threeAxis: next } };
              const id = assessmentIdRef.current;
              if (id) {
                saveAssessment(id, { result: updated, linkedinUrl, discipline, positionContext });
              }
              return updated;
            })
          }
          onMarketLevelChange={(next) =>
            setResult((prev) => {
              if (!prev) return prev;
              const updated = {
                ...prev,
                assessment: { ...prev.assessment, marketLevel: next },
              };
              const id = assessmentIdRef.current;
              if (id) {
                saveAssessment(id, { result: updated, linkedinUrl, discipline, positionContext });
              }
              return updated;
            })
          }
          onError={(message) =>
            toast({
              title: "Não foi possível salvar correção",
              description: message,
              variant: "destructive",
            })
          }
        />
      )}
    </>
  );
}

function ResultsView({
  result,
  onThreeAxisChange,
  onMarketLevelChange,
  onError,
}: {
  result: AssessCandidateResponse;
  onThreeAxisChange: (
    next: NonNullable<AssessCandidateResponse["assessment"]["threeAxis"]>,
  ) => void;
  onMarketLevelChange: (
    next: NonNullable<AssessCandidateResponse["assessment"]["marketLevel"]>,
  ) => void;
  onError: (message: string) => void;
}) {
  const threeAxis = result.assessment.threeAxis;
  // Use ONLY the archived labs_evaluations.id (a real UUID). The streaming
  // flow sends `done` before `archived`, so this is briefly undefined right
  // after the pipeline finishes — corrections submitted in that window used
  // to fall back to the LinkedIn URL hash and surface in the audit as
  // "(perfil removido)" until the orphan-relink job ran. We now gate the
  // pencil entirely on `archivedEvaluationId`: the report renders read-only
  // for the ~hundreds of milliseconds it takes to archive, and a tiny
  // status hint tells the user why.
  const archivedEvaluationId = result.archivedEvaluationId;
  const handleCorrection = useCorrectionHandler({
    assessmentId: archivedEvaluationId ?? "",
    threeAxis: threeAxis ?? undefined,
    setThreeAxis: onThreeAxisChange,
    onError,
  });
  const { isSuperAdmin, isManager, hasLabsAccess } = useAuth();
  // Mirrors the server gate on POST /api/labs/evaluations/:id/promote-to-fixture.
  // Same set as canOpenSettings — anyone with full labs access can promote
  // an evaluated profile to the calibration corpus.
  const canPromote = isSuperAdmin || isManager || hasLabsAccess;
  // Corrections write into CI's shared calibration corpus, so they stay a
  // build/DS action even though the assess/view surface is open to everyone.
  // Mirror the backend `submitCorrection` authorize (admin OR canAccessLabs) —
  // note this deliberately excludes the manager path that promote allows, since
  // `submitCorrection` does not grant it. When absent, the correction pencils
  // never render (the report is read-only for regular platform users).
  const canCorrect = isSuperAdmin || hasLabsAccess;
  // The "Histórico de mudanças" trigger + drawer used to live here next
  // to PromoteToFixtureBar, but it's an admin/meta action — reviewers
  // expected it next to the Settings gear at the top of the page, not
  // tucked into the actions bar above the report. Both live on the page
  // root now; this view only renders the promote bar + report.
  if (!threeAxis) return null;
  const candidateName = result.assessment.candidateName || "Candidato";
  return (
    <>
      {canPromote && (
        <div className="my-3">
          <PromoteToFixtureBar
            initialEvaluationId={archivedEvaluationId}
            candidateName={candidateName}
          />
        </div>
      )}
      {!archivedEvaluationId && canCorrect && (
        <div
          data-testid="results-archive-pending"
          className="inline-flex items-center gap-2 px-3 py-2 my-2 text-sm"
          style={{ color: "var(--ink-muted)", lineHeight: 1.5 }}
        >
          <span style={{ color: "var(--ink-primary)", fontWeight: 600 }}>Salvando…</span>
          <span>avaliação · os ajustes ficam disponíveis assim que ela é arquivada.</span>
        </div>
      )}
      <ThreeAxisReport
        recommendation={threeAxis}
        candidateName={candidateName}
        discipline={
          // `position.team` defaults to "—" on empty assessments
          // (shared/labs/candidate-assessment.ts) which slips past `|| undefined`
          // because the em-dash is truthy and bleeds into the hero eyebrow.
          // Treat the placeholder as missing so the default discipline kicks in.
          result.assessment.position?.team && result.assessment.position.team !== "—"
            ? result.assessment.position.team
            : undefined
        }
        linkedinUrl={result.assessment.linkedinUrl || undefined}
        onCorrection={archivedEvaluationId && canCorrect ? handleCorrection : undefined}
        marketLevel={result.assessment.marketLevel}
        archivedEvaluationId={archivedEvaluationId}
        onMarketLevelComputed={onMarketLevelChange}
      />
    </>
  );
}

/**
 * Tiny action strip that sits on top of the three-axis report and lets the
 * CHRO save the evaluated profile as a calibration fixture in one click.
 * The evaluation archive id comes from the assess response — once stored,
 * promotion copies the profile + extracts expected scores/skills from the
 * three-axis output.
 */
function PromoteToFixtureBar({
  initialEvaluationId,
  candidateName,
}: {
  initialEvaluationId?: string;
  candidateName: string;
}) {
  const [mode, setMode] = useState<"idle" | "choosing" | "done">("idle");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** What the operator just decided — drives the post-save success copy
   *  so "Pendente" doesn't read as "entrou na base como referência". */
  const [savedAs, setSavedAs] = useState<"hired" | "rejected" | "pending" | null>(null);
  // Resolved archive id: either the one shipped on the assess response, or
  // the latest matching row looked up on demand for cached sessions that
  // predate the response-side archive plumbing.
  const resolveEvaluationId = async (): Promise<string> => {
    if (initialEvaluationId) return initialEvaluationId;
    const data = (await rpcClient.labs.evaluationsList({ limit: 50 })) as {
      rows: { id: string; summary?: { recommendedLevel?: string } }[];
    };
    const hit = data.rows[0];
    if (!hit) {
      throw new Error(
        "avaliação ainda não foi arquivada — abra o perfil de novo e tente em alguns segundos",
      );
    }
    return hit.id;
  };
  const promote = async (decision: "hired" | "rejected" | "pending") => {
    setSaving(true);
    setError(null);
    try {
      const evaluationId = await resolveEvaluationId();
      await rpcClient.labs.evaluationsPromote({ id: evaluationId, decision });
      setSavedAs(decision);
      setMode("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "erro de rede");
    } finally {
      setSaving(false);
    }
  };

  if (mode === "done") {
    return (
      <div className="ci-stripe">
        <div
          data-testid="promote-bar-done"
          className="card"
          style={{ padding: "16px 24px", marginTop: 0 }}
        >
          <span style={{ color: "var(--r-ink)", lineHeight: 1.55, fontSize: 14 }}>
            {savedAs === "pending" ? (
              <>
                <strong style={{ color: "var(--r-ink)", fontWeight: 600 }}>Salvo</strong>{" "}
                <strong>{candidateName}</strong> como pendente. Vai ficar fora das calibrações até
                você decidir contratado/não contratado em "Perfis de referência".
              </>
            ) : savedAs === "rejected" ? (
              <>
                <strong style={{ color: "var(--r-forest)", fontWeight: 600 }}>Adicionado</strong>{" "}
                <strong>{candidateName}</strong> à base como contra-exemplo. Vamos usar este perfil
                para evitar erros parecidos nas próximas avaliações.
              </>
            ) : (
              <>
                <strong style={{ color: "var(--r-forest)", fontWeight: 600 }}>Adicionado</strong>{" "}
                <strong>{candidateName}</strong> ao conjunto de referência. Vamos usar este perfil
                para conferir as próximas avaliações.
              </>
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="ci-stripe">
      <div
        data-testid="promote-bar"
        className="card"
        style={{
          padding: "16px 24px",
          marginTop: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
          <strong style={{ color: "var(--r-ink)", fontSize: 14, fontWeight: 600 }}>
            Usar este perfil para melhorar a base
          </strong>
          <span style={{ color: "var(--r-ink-muted)", fontSize: 14, lineHeight: 1.55 }}>
            Guardamos esta avaliação como referência. Quando alguém ajustar uma nota, a gente
            confere contra {candidateName} e outros perfis de confiança antes de mudar o modelo.
          </span>
          {error && (
            <span className="pill alert no-dot" style={{ marginTop: 6, alignSelf: "flex-start" }}>
              Não foi possível: {error}
            </span>
          )}
        </div>
        {mode === "idle" ? (
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() => setMode("choosing")}
            data-testid="promote-bar-trigger"
          >
            Usar como referência
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ color: "var(--r-ink-muted)", fontSize: 14, marginRight: 4 }}>
              Contratamos esta pessoa?
            </span>
            <button
              type="button"
              className="btn primary"
              disabled={saving}
              onClick={() => promote("hired")}
              data-testid="promote-bar-yes"
            >
              Sim
            </button>
            <button
              type="button"
              className="btn"
              disabled={saving}
              onClick={() => promote("rejected")}
              data-testid="promote-bar-no"
            >
              Não
            </button>
            <button
              type="button"
              className="btn"
              disabled={saving}
              onClick={() => promote("pending")}
              data-testid="promote-bar-pending"
              title="Salva o perfil mas mantém fora das calibrações até você decidir"
            >
              Pendente
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={saving}
              onClick={() => setMode("idle")}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface PriorEvaluationRow {
  id: string;
  candidateName: string | null;
  disciplineSlug: string | null;
  targetLevel: string | null;
  createdBy: string | null;
  createdAt: string;
  summary: { recommendedLevel?: string };
}

function humanizeAge(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - then);
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) {
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours === 0) return "agora há pouco";
    if (hours === 1) return "há 1 hora";
    return `há ${hours} horas`;
  }
  if (days === 1) return "ontem";
  if (days < 30) return `há ${days} dias`;
  const months = Math.floor(days / 30);
  if (months === 1) return "há 1 mês";
  if (months < 12) return `há ${months} meses`;
  const years = Math.floor(months / 12);
  return years === 1 ? "há 1 ano" : `há ${years} anos`;
}

/**
 * Modal-style overlay shown when the CHRO confirms the preview and we
 * spot an earlier archive row for the same candidate. Two paths:
 *   - Abrir a anterior — loads the stored result as-is. No LLM burn.
 *   - Gerar de novo — proceeds with the full pipeline; the old row stays
 *     in the archive untouched (a new archive row is written on "done").
 */
function PriorEvaluationDialog({
  open,
  prior,
  onOpenPrior,
  onRegenerate,
  onCancel,
}: {
  open: boolean;
  prior: PriorEvaluationRow;
  onOpenPrior: () => void;
  onRegenerate: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  const age = humanizeAge(prior.createdAt);
  const fullDate = new Date(prior.createdAt).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const who = prior.candidateName ?? "Este candidato";
  // Three small meta atoms sit under the name. We only render non-null ones
  // so the row stays tight when we don't know the discipline or the
  // recommended level (older archives or PDF paths).
  const meta: { k: string; v: string }[] = [{ k: "avaliado em", v: `${fullDate} · ${age}` }];
  if (prior.disciplineSlug) {
    meta.push({ k: "posição", v: prior.disciplineSlug });
  }
  if (prior.summary.recommendedLevel) {
    meta.push({ k: "recomendação anterior", v: prior.summary.recommendedLevel });
  }
  return (
    <div className="ci-stripe">
      <div
        className="scrim"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prior-eval-title"
        onClick={onCancel}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
      >
        <div
          className="modal-card"
          role="document"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="card-head" style={{ alignItems: "flex-start", padding: "20px 24px" }}>
            <div className="card-head-left">
              <p
                className="eyebrow"
                style={{
                  font: "600 11px/1.4 'Inter', sans-serif",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--r-ink-muted)",
                  margin: "0 0 6px",
                }}
              >
                Histórico · avaliação encontrada
              </p>
              <h2
                id="prior-eval-title"
                style={{
                  margin: 0,
                  font: "600 20px/1.2 'Inter', sans-serif",
                  letterSpacing: "-0.01em",
                  color: "var(--r-ink)",
                }}
              >
                {who}
              </h2>
            </div>
            <div className="card-head-right">
              <button
                type="button"
                onClick={onCancel}
                aria-label="Fechar"
                data-testid="prior-eval-cancel"
                className="btn ghost"
                style={{ height: 32, padding: "0 8px", fontSize: 12 }}
              >
                Esc · fechar
              </button>
            </div>
          </div>

          <div className="card-body padded" style={{ padding: "20px 24px" }}>
            <dl style={{ display: "flex", flexDirection: "column", gap: 6, margin: 0 }}>
              {meta.map((m) => (
                <div key={m.k} style={{ display: "flex", gap: 8, fontSize: 14, lineHeight: 1.5 }}>
                  <dt style={{ color: "var(--r-ink-muted)", minWidth: 160 }}>{m.k}</dt>
                  <dd style={{ color: "var(--r-ink)", fontWeight: 600, margin: 0 }}>{m.v}</dd>
                </div>
              ))}
            </dl>

            <p
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--r-ink)",
                margin: "20px 0 0",
              }}
            >
              Abrir a avaliação anterior ou começar uma nova?
            </p>

            <p
              style={{
                fontSize: 12,
                color: "var(--r-ink-muted)",
                lineHeight: 1.55,
                margin: "12px 0 0",
              }}
            >
              Nada é apagado. A avaliação anterior continua no arquivo; a nova, se gerada, entra
              como registro independente.
            </p>
          </div>

          <div className="card-foot">
            <button
              type="button"
              className="btn"
              onClick={onRegenerate}
              data-testid="prior-eval-regenerate"
            >
              Gerar nova avaliação
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={onOpenPrior}
              data-testid="prior-eval-open"
            >
              Abrir a anterior
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
